import React, { useMemo } from 'react';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    faTriangleExclamation,
    faShieldHalved,
    faChevronDown,
    faChevronUp,
    faCopy,
    faEnvelope,
    faPhone,
    faLink,
} from '@fortawesome/free-solid-svg-icons';
import {
    ContactInfo,
    DecisionGuide,
    DrawerDecisionGuideFetchStatus,
    DrawerFacetIntelProps,
    CRITICAL_CONFIDENCE_THRESHOLD,
    ExtractionFields,
    InvoiceData,
    NoticeData,
    REVIEW_REASON_LABELS,
    ScamRisk,
} from './DrawerFacetTypes';
import DrawerDocumentFacts from './DrawerDocumentFacts';
import { documentAmountLabel } from '../utils/drawerDocumentFacts';
import DrawerDecisionGuideSection from './decisionGuide/DrawerDecisionGuideSection';
import { formatHumanDrawerDate } from '../utils/deriveNextStep';

// ── Content predicates (shared with the drawer so the tab and the facet never drift) ──

export interface IntelContentInput {
    decisionGuideFetchStatus: DrawerDecisionGuideFetchStatus;
    decisionGuide: DecisionGuide | null;
    scamRisk: ScamRisk | null;
    invoiceData: InvoiceData | null;
    contactInfo: ContactInfo | null;
    noticeData: NoticeData | null;
    extractionFields: ExtractionFields | null;
    reviewRequired: boolean;
    reviewReasons: string[];
}

type CriticalFieldEntry = {
    key: 'amount_due' | 'due_date' | 'reference_id' | 'document_date';
    label: string;
    field: ExtractionFields[keyof ExtractionFields] | undefined;
};

function buildCriticalFieldEntries(extractionFields: ExtractionFields | null): CriticalFieldEntry[] {
    const fields = extractionFields || {};
    return [
        { key: 'amount_due', label: documentAmountLabel(fields.amount_due?.evidence || ''), field: fields.amount_due },
        { key: 'due_date', label: 'Due date', field: fields.due_date },
        { key: 'reference_id', label: 'Reference ID', field: fields.reference_id },
        { key: 'document_date', label: 'Document date', field: fields.document_date },
    ];
}

function buildReviewReasonLabels(reviewReasons: string[], entries: CriticalFieldEntry[]): string[] {
    const base = (reviewReasons || []).map((reason) => REVIEW_REASON_LABELS[reason] || reason);
    const lowConfidence = entries
        .filter((entry) => {
            const confidence = entry.field?.confidence;
            return entry.field?.value !== null
                && entry.field?.value !== undefined
                && typeof confidence === 'number'
                && confidence > 0
                && confidence < CRITICAL_CONFIDENCE_THRESHOLD;
        })
        .map((entry) => `Low confidence in ${entry.label.toLowerCase()}`);
    return Array.from(new Set([...base, ...lowConfidence].filter(Boolean)));
}

function entriesHaveCriticalFields(entries: CriticalFieldEntry[]): boolean {
    return entries.some((entry) => (
        (entry.field?.value !== null && entry.field?.value !== undefined)
        || Boolean(entry.field?.evidence)
    ));
}

function hasInvoiceContent(invoiceData: InvoiceData | null): boolean {
    const amount = invoiceData?.total_amount || invoiceData?.amount || null;
    return Boolean(
        invoiceData
        && (invoiceData.vendor || amount || invoiceData.due_date || invoiceData.invoice_number)
    );
}

function hasContactContent(contactInfo: ContactInfo | null): boolean {
    return Boolean(
        contactInfo
        && ((contactInfo.emails?.length || 0) > 0
            || (contactInfo.phones?.length || 0) > 0
            || (contactInfo.websites?.length || 0) > 0)
    );
}

function hasNoticeContent(noticeData: NoticeData | null): boolean {
    return Boolean(
        noticeData
        && (noticeData.agency_or_sender
            || noticeData.notice_id
            || noticeData.case_id
            || noticeData.action_required
            || noticeData.response_deadline
            || (noticeData.consequences_keywords?.length || 0) > 0)
    );
}

/** Decision-guide block is on screen while loading / errored (retry) / loaded with a guide. */
function decisionGuideSurfaceIsActive(
    status: DrawerDecisionGuideFetchStatus,
    decisionGuide: DecisionGuide | null,
): boolean {
    return status === 'loading'
        || status === 'error'
        || (status === 'success' && decisionGuide != null);
}

/**
 * True when the Evidence facet would render anything other than its empty state.
 * The drawer uses this to hide the tab entirely — no dead rooms.
 */
export function hasIntelContent(input: IntelContentInput): boolean {
    const entries = buildCriticalFieldEntries(input.extractionFields);
    const reviewLabels = buildReviewReasonLabels(input.reviewReasons, entries);
    return Boolean(
        input.reviewRequired
        || reviewLabels.length > 0
        || entriesHaveCriticalFields(entries)
        || hasInvoiceContent(input.invoiceData)
        || hasNoticeContent(input.noticeData)
        || hasContactContent(input.contactInfo)
        || (input.scamRisk && input.scamRisk.level !== 'low')
        || decisionGuideSurfaceIsActive(input.decisionGuideFetchStatus, input.decisionGuide),
    );
}

/**
 * 🛡️ Evidence facet — document intelligence and decision support.
 * Shows: review banner, critical fields, decision guide, invoice/notice/contact, scam details.
 * Read-heavy context presented as intelligence briefing cards.
 */
const DrawerFacetIntel: React.FC<DrawerFacetIntelProps> = ({
    item,
    draft,
    decisionGuideFetchStatus,
    onRetryDecisionGuide,
    decisionGuide,
    scamRisk,
    invoiceData,
    contactInfo,
    noticeData,
    extractionFields,
    reviewRequired,
    reviewReasons,
    scamExpanded,
    onToggleScamDetails,
    onCopy,
}) => {
    // ── Derived state ───────────────────────────────────────────────────
    const hasInvoiceData = hasInvoiceContent(invoiceData);
    const hasContactInfo = hasContactContent(contactInfo);
    const hasNoticeData = hasNoticeContent(noticeData);

    const criticalFieldEntries = useMemo(() => buildCriticalFieldEntries(extractionFields), [extractionFields]);

    const reviewReasonLabels = useMemo(
        () => buildReviewReasonLabels(reviewReasons, criticalFieldEntries),
        [reviewReasons, criticalFieldEntries],
    );

    const showReviewBanner = reviewRequired || reviewReasonLabels.length > 0;

    const scamLevel = scamRisk?.level || 'low';
    const scamIsHigh = scamLevel === 'high';
    const scamIsMedium = scamLevel === 'medium';
    const scamShowDetails = Boolean(scamRisk && (scamIsHigh || scamExpanded));

    // ── Render helpers ──────────────────────────────────────────────────
    const renderContactList = (label: string, icon: IconProp, items?: string[]) => {
        if (!items || items.length === 0) return null;
        return (
            <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                    {label}
                </Typography>
                {items.map((itemValue) => (
                    <Box
                        key={`${label}-${itemValue}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            p: 1,
                            borderRadius: 2,
                            border: '1px solid var(--color-border)',
                            bgcolor: 'var(--color-bg-card)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <FontAwesomeIcon icon={icon} style={{ color: 'var(--color-text-muted)' }} />
                            <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>
                                {itemValue}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => onCopy(itemValue)} aria-label={`Copy ${label}`}>
                            <FontAwesomeIcon icon={faCopy} />
                        </IconButton>
                    </Box>
                ))}
            </Box>
        );
    };

    const noticeDeadline = noticeData?.response_deadline
        ? formatHumanDrawerDate(noticeData.response_deadline) || noticeData.response_deadline
        : null;
    const noticeRows = [
        { label: 'Agency / sender', value: noticeData?.agency_or_sender || null, urgent: false },
        { label: 'Notice / case ID', value: noticeData?.notice_id || noticeData?.case_id || null, urgent: false },
        { label: 'Response deadline', value: noticeDeadline, urgent: true },
        { label: 'Action required', value: noticeData?.action_required || null, urgent: false },
    ].filter((row): row is { label: string; value: string; urgent: boolean } => Boolean(row.value));

    // Same predicate the drawer uses to decide whether the Evidence tab exists at all.
    const hasAnyIntel = hasIntelContent({
        decisionGuideFetchStatus,
        decisionGuide,
        scamRisk,
        invoiceData,
        contactInfo,
        noticeData,
        extractionFields,
        reviewRequired,
        reviewReasons,
    });

    if (!hasAnyIntel) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: '2rem', color: 'var(--color-text-muted)', opacity: 0.3 }} />
                <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mt: 2 }}>
                    No document intelligence available for this item.
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', mt: 0.5, display: 'block' }}>
                    Intel data is extracted when documents are uploaded or emails are synced.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'grid', gap: 2.5 }}>
            {/* Review required banner */}
            {showReviewBanner && (
                <Box sx={{ p: 2, borderRadius: 3, border: '1px solid rgba(var(--color-status-triage-rgb), 0.35)', bgcolor: 'rgba(var(--color-status-triage-rgb), 0.08)', display: 'grid', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: 'var(--color-status-triage)' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
                                Review required
                            </Typography>
                        </Box>
                        <Chip size="small" variant="outlined" label="Needs review" sx={{ bgcolor: 'transparent', color: 'var(--color-status-triage)', borderColor: 'var(--color-status-triage)', fontWeight: 700 }} />
                    </Box>
                    {reviewReasonLabels.length > 0 && (
                        <Box sx={{ display: 'grid', gap: 0.5 }}>
                            {reviewReasonLabels.map((reason) => (
                                <Typography key={reason} variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                                    • {reason}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            <DrawerDocumentFacts title={item?.title} invoiceData={invoiceData} extractionFields={extractionFields} showEvidence />

            {/* Scam Risk */}
            {scamRisk && scamRisk.level !== 'low' && (
                <Box
                    sx={{
                        p: scamIsHigh ? 2 : 1.25,
                        borderRadius: 3,
                        bgcolor: scamIsHigh
                            ? 'rgba(var(--color-danger-rgb), 0.06)'
                            : scamIsMedium
                                ? 'rgba(var(--color-status-triage-rgb), 0.08)'
                                : 'var(--color-bg-card)',
                        border: `1px solid ${scamIsHigh
                            ? 'rgba(var(--color-danger-rgb), 0.25)'
                            : scamIsMedium
                                ? 'rgba(var(--color-status-triage-rgb), 0.3)'
                                : 'var(--color-border)'}`,
                        display: scamShowDetails ? 'grid' : 'flex',
                        gap: scamShowDetails ? 1.5 : 0.5,
                        alignItems: scamShowDetails ? 'stretch' : 'center',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FontAwesomeIcon
                                icon={scamIsHigh || scamIsMedium ? faTriangleExclamation : faShieldHalved}
                                style={{ color: scamIsHigh ? 'var(--color-danger)' : scamIsMedium ? 'var(--color-status-triage)' : 'var(--color-text-muted)' }}
                            />
                            <Typography
                                variant={scamIsHigh ? 'subtitle2' : 'caption'}
                                sx={{
                                    fontWeight: 700,
                                    color: 'var(--color-text-primary)',
                                    textTransform: scamIsHigh ? 'none' : 'uppercase',
                                    letterSpacing: scamIsHigh ? 0 : '0.08em',
                                }}
                            >
                                {scamIsHigh ? 'Scam risk alert' : 'Scam risk check'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                size="small"
                                variant="outlined"
                                label={scamRisk.level.toUpperCase()}
                                sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    // Outlined on the tinted card: light tokens are sized for 4.5:1 on that composite.
                                    color: scamIsHigh ? 'var(--color-danger)' : scamIsMedium ? 'var(--color-status-triage)' : 'var(--color-text-secondary)',
                                    borderColor: scamIsHigh ? 'var(--color-danger)' : scamIsMedium ? 'var(--color-status-triage)' : 'var(--color-border)',
                                    bgcolor: 'transparent',
                                }}
                            />
                            {!scamIsHigh && (
                                <IconButton size="small" onClick={onToggleScamDetails}>
                                    <FontAwesomeIcon icon={scamExpanded ? faChevronUp : faChevronDown} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                    {scamShowDetails && (
                        <>
                            <Box sx={{ display: 'grid', gap: 0.75 }}>
                                {(scamRisk.reasons || []).map((reason, idx) => (
                                    <Typography key={idx} variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                                        • {reason}
                                    </Typography>
                                ))}
                            </Box>
                            {scamRisk.recommended_first_step && (
                                <Typography variant="caption" sx={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
                                    Safe first step: {scamRisk.recommended_first_step}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            )}

            <DrawerDecisionGuideSection
                readOnly={['DONE', 'ARCHIVED'].includes(item?.state || '') || scamIsHigh}
                status={decisionGuideFetchStatus}
                decisionGuide={decisionGuide}
                onCopy={onCopy}
                onRetry={onRetryDecisionGuide}
            />

            {/* Invoice / Notice / Contact cards */}
            {(hasInvoiceData || hasNoticeData || hasContactInfo) && (
                <Box sx={{ display: 'grid', gap: 2 }}>
                    {hasNoticeData && (
                        <Box sx={{ p: 2, borderRadius: 3, border: '1px solid var(--color-border)', bgcolor: 'var(--color-bg-card)' }}>
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Notice details
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mt: 1.5 }}>
                                {/* Only facts the notice states; an empty field is not shown as "N/A". */}
                                {noticeRows.map((row) => (
                                    <Box key={row.label}>
                                        <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block' }}>{row.label}</Typography>
                                        <Typography variant="body2" sx={{ color: row.urgent ? 'var(--color-status-triage)' : 'var(--color-text-primary)', fontWeight: 600 }}>
                                            {row.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            {(noticeData?.consequences_keywords?.length || 0) > 0 && (
                                <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {noticeData?.consequences_keywords?.map((keyword) => (
                                        <Chip key={keyword} size="small" variant="outlined" label={keyword} sx={{ bgcolor: 'transparent', color: 'var(--color-danger)', borderColor: 'var(--color-danger)', fontWeight: 600 }} />
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    {hasContactInfo && (
                        <Box sx={{ p: 2, borderRadius: 3, border: '1px solid var(--color-border)', bgcolor: 'var(--color-bg-card)', display: 'grid', gap: 1.5 }}>
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Contact info
                            </Typography>
                            <Box sx={{ display: 'grid', gap: 1.5 }}>
                                {renderContactList('Emails', faEnvelope, contactInfo?.emails)}
                                {renderContactList('Phones', faPhone, contactInfo?.phones)}
                                {renderContactList('Websites', faLink, contactInfo?.websites)}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default DrawerFacetIntel;
